<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { getCurrentLocale } from '@/i18n';
import { useCheckMaxPassengersComposable } from '../useCheckMaxPassengersComposable';
import { useBookComposable } from '../useBookComposable';
import { useModalComposable } from '@/views/composable';
import { getTextVariant } from '@/utils/stringUtils';
import Button from 'primevue/button';
import ColumnGroup from 'primevue/columngroup';
import DataTable from 'primevue/datatable';
import InputNumber from 'primevue/inputnumber';
import Row from 'primevue/row';
import TableColumn from 'primevue/column';
import CruisePaxName from '../CruisePaxName.vue';
import DetailedOccupancy from '@/components/book/DetailedOccupancy.vue';

const props = defineProps<{
  dataCabins: object;
  cabinType: object;
  isManagement: boolean;
  priorityStatus: number | undefined;
  isShowAddPax?: boolean;
  cabinDetailOccupancies?: any;
}>();

const emits = defineEmits<{
  addPax: [value: any];
  submitCruiseQuote: [];
  'update:dataCabins': [value: any];
  'update:cabinType': [value: any];
}>();

const { t } = useI18n({ useScope: 'global' });
const { handleOpenErrorMessage } = useModalComposable();
const currentLanguage = getCurrentLocale();
const editingRows = ref([]);
const isShowDetailOccupancy = ref(true);
const isShowAddPaxCabin = ref<boolean>(false);
const resultTotals: any = ref([]);

const { checkMaxCabin } = useCheckMaxPassengersComposable();
const { calculateRoomTotals, hasViewOccupacyPermission, hasEditBookPermission, checkPriorityStatus, checkCabinCode } = useBookComposable();

const dataCabins = computed({
  get() {
    return props.dataCabins;
  },
  set(value) {
    emits('update:dataCabins', value);
  }
});

const cabinType = computed({
  get() {
    return props.cabinType;
  },
  set(value) {
    emits('update:cabinType', value);
  }
});

const onRowEditSave = async ({ newData, index }, cabinInfos: any, dataCommon: any) => {
  isShowDetailOccupancy.value = false;
  const item = [...cabinInfos];
  item[index] = { ...newData };
  const checkOver = checkMaxCabin(item, dataCommon.cabinNo, dataCommon.adt);
  if (checkOver) {
    handleOpenErrorMessage(t('my_quote.toastMessage.over_selected_cabin'));
  } else {
    cabinInfos[index] = { ...newData };
    await emits('submitCruiseQuote');
  }
  isShowDetailOccupancy.value = true;
};

const addPax = (dataOption: any, component: any, cruiseIndex: number, index: number, retDate: any, callback = undefined, hotelType = '') => {
  const cabinOccupancy = findCabinDetailOccupancy(props.cabinDetailOccupancies, dataOption.cabinType);
  emits('addPax', {
    dataOption: dataOption,
    cabinOccupancy: cabinOccupancy,
    component: component,
    cruiseIndex: cruiseIndex,
    index: index,
    retDate: retDate,
    callback: callback,
    hotelType: hotelType
  });
};

const calculateTotalNo = (index: number) => {
  const cabinRoomInfo = dataCabins.value.data.cabinInfos[index];
  let totalRoom = 0;
  for (const cabinKey in cabinRoomInfo) {
    if (cabinKey.endsWith('No') && typeof cabinRoomInfo[cabinKey] === 'number') {
      totalRoom += cabinRoomInfo[cabinKey];
    }
  }
  if (totalRoom) {
    isShowAddPaxCabin.value = true;
  }
  return totalRoom;
};

const getTotalChildCabinInRoom = (cabinIndex: number, roomType: string, personType: string) => {
  if (resultTotals.value && resultTotals.value.length) {
    return resultTotals.value.find((item: any) => item.cabinIndex === cabinIndex)?.[roomType]?.[personType] ?? 0;
  }
  return 0;
};

const updateCabinDetailOccupancies = (data: any, event: any, cabinIndex: number) => {
  const dataCabin: any = [];
  data.cabinInfos?.forEach((cabin: any, index: number) => {
    const oldOccupancies = data?.cabinDetailOccupancies?.[index]?.detailedOccupancy || [];
    if (cabin.cabinType) {
      dataCabin.push({
        detailedOccupancy: index === cabinIndex ? event : oldOccupancies,
        cabinType: cabin.cabinType
      });
    }
  });

  return dataCabin;
};

const updateCabin = (data: any) => {
  const cabinRoomTotals = calculateRoomTotals(data.event);
  const newCabinEntry = {
    cabinIndex: data.cabinIndex,
    ...cabinRoomTotals
  };

  const existingResult = resultTotals.value.findIndex((item: any) => item.cabinIndex === newCabinEntry.cabinIndex);

  if (existingResult > -1) {
    resultTotals.value[existingResult] = newCabinEntry;
  } else {
    resultTotals.value.push(newCabinEntry);
  }
};

const findCabinDetailOccupancy = (cabinDetailOccupancies: any, cabinType: string) => {
  return cabinDetailOccupancies?.find((cabin: any) => cabin.cabinType === cabinType)?.detailedOccupancy;
};

const updateBookingRequestForm = (event: any, data: any, cabinType: string, cabinIndex: number) => {
  updateCabin({ event, cabinIndex });
  data.cabinDetailOccupancies = updateCabinDetailOccupancies(data, event, cabinIndex);
};

const formatCabinName = (cabinCode: string) => {
  const foundCabin = dataCabins.value.data.cabinCategory.find((cabin: any) => cabin.code === cabinCode);
  return foundCabin?.code && (foundCabin?.nameFr || foundCabin?.nameEn) ? `${getTextVariant(currentLanguage, foundCabin.nameEn, foundCabin.nameFr)} - ${foundCabin.code}` : checkCabinCode(cabinCode);
};

const changePaxCabin = (cabinInfos: any) => {
  const showDetail = cabinInfos.some((_: any, index: number) => {
    return calculateTotalNo(index);
  });

  isShowAddPaxCabin.value = showDetail;
};

watch(
  () => props.dataCabins,
  (newValue) => {
    changePaxCabin(newValue.data.cabinInfos);
  },
  { deep: true }
);
</script>
<template>
  <div class="cruise-option-cabin-table">
    <DataTable
      :value="dataCabins.data.cabinInfos"
      editMode="row"
      v-model:editingRows="editingRows"
      @row-edit-save="onRowEditSave($event, dataCabins.data.cabinInfos, dataCabins.data)"
      class="editable-cells-table bg-blue-50 shadow-2"
      responsiveLayout="stack"
    >
      <ColumnGroup type="header" v-if="isShowAddPaxCabin">
        <Row>
          <TableColumn field="room" :header="$t('book_form.room_category')" :rowspan="1" />
          <TableColumn :header="$t('book_form.single')" :colspan="3" />
          <TableColumn :header="$t('book_form.double')" :colspan="3" />
          <TableColumn :header="$t('book_form.triple')" :colspan="3" />
          <TableColumn :header="$t('book_form.quad')" :colspan="3" />
          <TableColumn :header="$t('book_form.five')" :colspan="3" />
          <TableColumn :header="$t('book_form.six')" :colspan="3" />
          <TableColumn :header="$t('common.edit')" :colspan="1" />
          <TableColumn :header="$t('common.action')" :colspan="1" />
        </Row>
        <Row>
          <TableColumn :colspan="1" />
          <TableColumn class="width-column-group">
            <template #header>
              <p class="room-font-size">{{ $t('book_form.pound_sign_rooms') }}</p>
            </template>
          </TableColumn>
          <TableColumn class="width-column-group">
            <template #header>
              <p class="room-font-size">{{ $t('book_form.pound_sign_children') }}</p>
            </template>
          </TableColumn>
          <TableColumn class="width-column-group">
            <template #header>
              <p class="room-font-size">{{ $t('book_form.pound_sign_infants') }}</p>
            </template>
          </TableColumn>
          <TableColumn class="width-column-group">
            <template #header>
              <p class="room-font-size">{{ $t('book_form.pound_sign_rooms') }}</p>
            </template>
          </TableColumn>
          <TableColumn class="width-column-group">
            <template #header>
              <p class="room-font-size">{{ $t('book_form.pound_sign_children') }}</p>
            </template>
          </TableColumn>
          <TableColumn class="width-column-group">
            <template #header>
              <p class="room-font-size">{{ $t('book_form.pound_sign_infants') }}</p>
            </template>
          </TableColumn>
          <TableColumn class="width-column-group">
            <template #header>
              <p class="room-font-size">{{ $t('book_form.pound_sign_rooms') }}</p>
            </template>
          </TableColumn>
          <TableColumn class="width-column-group">
            <template #header>
              <p class="room-font-size">{{ $t('book_form.pound_sign_children') }}</p>
            </template>
          </TableColumn>
          <TableColumn class="width-column-group">
            <template #header>
              <p class="room-font-size">{{ $t('book_form.pound_sign_infants') }}</p>
            </template>
          </TableColumn>
          <TableColumn class="width-column-group">
            <template #header>
              <p class="room-font-size">{{ $t('book_form.pound_sign_rooms') }}</p>
            </template>
          </TableColumn>
          <TableColumn class="width-column-group">
            <template #header>
              <p class="room-font-size">{{ $t('book_form.pound_sign_children') }}</p>
            </template>
          </TableColumn>
          <TableColumn class="width-column-group">
            <template #header>
              <p class="room-font-size">{{ $t('book_form.pound_sign_infants') }}</p>
            </template>
          </TableColumn>
          <TableColumn class="width-column-group">
            <template #header>
              <p class="room-font-size">{{ $t('book_form.pound_sign_rooms') }}</p>
            </template>
          </TableColumn>
          <TableColumn class="width-column-group">
            <template #header>
              <p class="room-font-size">{{ $t('book_form.pound_sign_children') }}</p>
            </template>
          </TableColumn>
          <TableColumn class="width-column-group">
            <template #header>
              <p class="room-font-size">{{ $t('book_form.pound_sign_infants') }}</p>
            </template>
          </TableColumn>
          <TableColumn class="width-column-group">
            <template #header>
              <p class="room-font-size">{{ $t('book_form.pound_sign_rooms') }}</p>
            </template>
          </TableColumn>
          <TableColumn class="width-column-group">
            <template #header>
              <p class="room-font-size">{{ $t('book_form.pound_sign_children') }}</p>
            </template>
          </TableColumn>
          <TableColumn class="width-column-group">
            <template #header>
              <p class="room-font-size">{{ $t('book_form.pound_sign_infants') }}</p>
            </template>
          </TableColumn>
          <TableColumn :colspan="1" />
          <TableColumn :colspan="1" />
        </Row>
      </ColumnGroup>
      <TableColumn field="cabinType" :header="$t('book_form.cabin_type')" class="py-3">
        <template #body="{ data, field }">
          <span>{{ formatCabinName(data[field]) }}</span>
        </template>
      </TableColumn>
      <TableColumn field="singleNo" :header="$t('book_form.single')">
        <template #editor="{ data, field }">
          <InputNumber :max="1000" :min="0" v-model="data[field]" autofocus :useGrouping="false" />
        </template>
        <template #body="{ data, field }">
          <p>{{ data[field] || 0 }}</p>
        </template>
      </TableColumn>
      <TableColumn v-if="isShowAddPaxCabin" class="width-column-group">
        <template #body="{ index }">
          <p>{{ getTotalChildCabinInRoom(index, 'singleRoom', 'children') }}</p>
        </template>
      </TableColumn>
      <TableColumn v-if="isShowAddPaxCabin" class="width-column-group">
        <template #body="{ index }">
          <p>{{ getTotalChildCabinInRoom(index, 'singleRoom', 'infants') }}</p>
        </template>
      </TableColumn>
      <TableColumn field="doubleNo" :header="$t('book_form.double')">
        <template #editor="{ data, field }">
          <InputNumber :max="1000" :min="0" v-model="data[field]" autofocus :useGrouping="false" />
        </template>
        <template #body="{ data, field }">
          <p>{{ data[field] || 0 }}</p>
        </template>
      </TableColumn>
      <TableColumn v-if="isShowAddPaxCabin" class="width-column-group">
        <template #body="{ index }">
          <p>{{ getTotalChildCabinInRoom(index, 'doubleRoom', 'children') }}</p>
        </template>
      </TableColumn>
      <TableColumn v-if="isShowAddPaxCabin" class="width-column-group">
        <template #body="{ index }">
          <p>{{ getTotalChildCabinInRoom(index, 'doubleRoom', 'infants') }}</p>
        </template>
      </TableColumn>
      <TableColumn field="tripleNo" :header="$t('book_form.triple')">
        <template #editor="{ data, field }">
          <InputNumber :max="1000" :min="0" v-model="data[field]" autofocus :useGrouping="false" />
        </template>
        <template #body="{ data, field }">
          <p>{{ data[field] || 0 }}</p>
        </template>
      </TableColumn>
      <TableColumn v-if="isShowAddPaxCabin" class="width-column-group">
        <template #body="{ index }">
          <p>{{ getTotalChildCabinInRoom(index, 'tripleRoom', 'children') }}</p>
        </template>
      </TableColumn>
      <TableColumn v-if="isShowAddPaxCabin" class="width-column-group">
        <template #body="{ index }">
          <p>{{ getTotalChildCabinInRoom(index, 'tripleRoom', 'infants') }}</p>
        </template>
      </TableColumn>
      <TableColumn field="quadNo" :header="$t('book_form.quad')">
        <template #editor="{ data, field }">
          <InputNumber :max="1000" :min="0" v-model="data[field]" autofocus :useGrouping="false" />
        </template>
        <template #body="{ data, field }">
          <p>{{ data[field] || 0 }}</p>
        </template>
      </TableColumn>
      <TableColumn v-if="isShowAddPaxCabin" class="width-column-group">
        <template #body="{ index }">
          <p>{{ getTotalChildCabinInRoom(index, 'quadRoom', 'children') }}</p>
        </template>
      </TableColumn>
      <TableColumn v-if="isShowAddPaxCabin" class="width-column-group">
        <template #body="{ index }">
          <p>{{ getTotalChildCabinInRoom(index, 'quadRoom', 'infants') }}</p>
        </template>
      </TableColumn>
      <TableColumn field="fiveInRoomNo" :header="$t('book_form.five')">
        <template #editor="{ data, field }">
          <InputNumber :max="1000" :min="0" :disabled="data.cabinType !== cabinType.suite" v-model="data[field]" autofocus :useGrouping="false" />
        </template>
        <template #body="{ data, field }">
          <p>{{ data[field] || 0 }}</p>
        </template>
      </TableColumn>
      <TableColumn v-if="isShowAddPaxCabin" class="width-column-group">
        <template #body="{ index }">
          <p>{{ getTotalChildCabinInRoom(index, 'fiveRoom', 'children') }}</p>
        </template>
      </TableColumn>
      <TableColumn v-if="isShowAddPaxCabin" class="width-column-group">
        <template #body="{ index }">
          <p>{{ getTotalChildCabinInRoom(index, 'fiveRoom', 'infants') }}</p>
        </template>
      </TableColumn>
      <TableColumn field="sixInRoomNo" :header="$t('book_form.six')">
        <template #editor="{ data, field }">
          <InputNumber :max="1000" :min="0" :disabled="data.cabinType !== cabinType.suite" v-model="data[field]" autofocus :useGrouping="false" />
        </template>
        <template #body="{ data, field }">
          <p>{{ data[field] || 0 }}</p>
        </template>
      </TableColumn>
      <TableColumn v-if="isShowAddPaxCabin" class="width-column-group">
        <template #body="{ index }">
          <p>{{ getTotalChildCabinInRoom(index, 'sixRoom', 'children') }}</p>
        </template>
      </TableColumn>
      <TableColumn v-if="isShowAddPaxCabin" class="width-column-group">
        <template #body="{ index }">
          <p>{{ getTotalChildCabinInRoom(index, 'sixRoom', 'infants') }}</p>
        </template>
      </TableColumn>
      <TableColumn v-if="hasEditBookPermission && !checkPriorityStatus(props.priorityStatus, props.isManagement)" :header="$t('common.edit')" :rowEditor="true" class="py-3"></TableColumn>
      <TableColumn v-if="hasViewOccupacyPermission" :header="$t('common.action')" class="py-3 text-center">
        <template #body="{ data, index }">
          <Button
            v-if="calculateTotalNo(index)"
            :label="isManagement ? $t('book_form.edit_pax') : $t('book_form.add_pax')"
            @click="addPax(data, CruisePaxName, dataCabins.index, index, dataCabins.data.retDate, undefined, '')"
            class="p-button-outlined cruise-add-pax"
          />
        </template>
      </TableColumn>
    </DataTable>
    <div v-if="isShowDetailOccupancy">
      <div v-for="(cabinInfo, cabinIndex) in dataCabins.data.cabinInfos" :key="cabinIndex" class="mt-4">
        <DetailedOccupancy
          :cabinCategory="dataCabins.data.cabinCategory"
          :cabin="cabinInfo"
          :isCabin="true"
          :cabinType="cabinInfo.cabinType"
          :cabinDetail="dataCabins.data.cabins[cabinIndex]"
          :detailedOccupancy="findCabinDetailOccupancy(dataCabins.data.cabinDetailOccupancies, cabinInfo.cabinType)"
          @updateBookingRequestForm="updateBookingRequestForm($event, dataCabins.data, cabinInfo.cabinType, cabinIndex)"
        ></DetailedOccupancy>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
:deep(.text-center .p-column-header-content) {
  justify-content: center;
}
.pre-line {
  white-space: pre-line;
}
:deep() {
  .room-font-size {
    font-size: 10px;
  }
}
</style>
